import React, { useRef, useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { NumberInput, Slider, Checkbox } from '../ui/Input';
import { DRAINAGE_MODES, bearingLabel } from '../../utils/grading';

const ARENA_PRESETS = [
  { label: '100×200', width: 100, length: 200 },
  { label: '120×240', width: 120, length: 240 },
  { label: '20×40 m', width: 66, length: 131 },
  { label: '20×60 m', width: 66, length: 197 },
];

const muted = { color: '#6080a0', fontSize: '11px', lineHeight: 1.5 };
const yards = (v) => `${Math.round(v).toLocaleString()}`;
const feet = (v) => `${v.toFixed(1)}'`;

/**
 * GradingPanel - existing ground from drone data, arena outline, drainage
 * options and cut/fill comparison
 */
export const GradingPanel = ({
  terrain,
  hasGeoLayer,
  rooms,
  grading,
  onChangeGrading,
  onLoadTerrain,
  onRemoveTerrain,
  onAddArena,
  arenaShape,
  onResizeArena,
  result,
  onShow3D,
  onClose,
  isMobile = false,
}) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const set = (updates) => onChangeGrading({ ...grading, ...updates });
  const outlineRooms = rooms.filter(r => r.points?.length >= 3);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      await onLoadTerrain(file);
    } catch (err) {
      console.error('Failed to load terrain:', err);
      setError(err.message || 'Failed to load elevation data');
    } finally {
      setLoading(false);
    }
  };

  const active = result?.active;
  const params = active?.params;

  return (
    <FloatingPanel
      title="Site Grading"
      onClose={onClose}
      x={isMobile ? 10 : 100}
      y={isMobile ? 60 : 80}
      width={isMobile ? 300 : 330}
      maxHeight={window.innerHeight - 160}
      isMobile={isMobile}
    >
      <div style={{ padding: '12px 14px' }}>
        <input ref={fileInputRef} type="file" accept=".ply" onChange={handleFile} style={{ display: 'none' }} />

        {error && (
          <div style={{
            background: 'rgba(255,80,80,0.2)', border: '1px solid rgba(255,80,80,0.5)', borderRadius: '6px',
            padding: '8px', marginBottom: '12px', color: '#ff6666', fontSize: '11px',
          }}>
            {error}
          </div>
        )}

        <PanelSection title="Existing ground">
          {!hasGeoLayer ? (
            <p style={muted}>
              Import the drone orthophoto first (PDF button → select the image and its .jgw world file).
              The elevation data is placed using the same survey coordinates.
            </p>
          ) : terrain ? (
            <>
              <p style={{ ...muted, color: '#a0b0c0' }}>
                {terrain.fileName} · {terrain.cols}×{terrain.rows} grid
              </p>
              <Button variant="danger" onClick={onRemoveTerrain} style={{ width: '100%', fontSize: '11px' }}>
                Remove elevation data
              </Button>
            </>
          ) : (
            <>
              <p style={muted}>
                Load the mesh exported from 3Dsurvey (binary .ply). It is turned into a 1 ft elevation grid.
              </p>
              <Button
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                style={{ width: '100%' }}
              >
                {loading ? 'Processing mesh…' : 'Load elevation mesh (.ply)'}
              </Button>
            </>
          )}
        </PanelSection>

        {terrain && hasGeoLayer && (
          <>
            <PanelSection title="Arena outline">
              {outlineRooms.length > 0 && (
                <select
                  value={grading.roomId || ''}
                  onChange={(e) => set({ roomId: e.target.value || null })}
                  style={{
                    width: '100%', padding: '8px', marginBottom: '8px', background: '#1a2030', color: '#fff',
                    border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px',
                  }}
                >
                  <option value="">Choose an outline…</option>
                  {outlineRooms.map(r => <option key={r.id} value={r.id}>{r.name || 'Room'}</option>)}
                </select>
              )}
              <Button variant="default" onClick={onAddArena} style={{ width: '100%', fontSize: '11px', marginBottom: '8px' }}>
                Add arena outline at view center
              </Button>

              {arenaShape && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' }}>
                    <NumberInput label="Width (ft)" value={Math.round(arenaShape.widthFt * 10) / 10} min={10}
                      onChange={(v) => onResizeArena({ widthFt: v })} style={{ padding: '6px', fontSize: '12px' }} />
                    <NumberInput label="Length (ft)" value={Math.round(arenaShape.lengthFt * 10) / 10} min={10}
                      onChange={(v) => onResizeArena({ lengthFt: v })} style={{ padding: '6px', fontSize: '12px' }} />
                    <NumberInput label="Rotate (°)" value={Math.round(arenaShape.angleDeg * 10) / 10}
                      onChange={(v) => onResizeArena({ angleDeg: v })} style={{ padding: '6px', fontSize: '12px' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    {ARENA_PRESETS.map(p => (
                      <Button key={p.label} variant="default" style={{ fontSize: '10px', padding: '4px 6px' }}
                        onClick={() => onResizeArena({ widthFt: p.width, lengthFt: p.length })}>
                        {p.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              <p style={muted}>
                Drag the outline with the Select tool to move it. Any room drawn with the Room tool can be used.
                Trees and buildings in the survey count as ground, so keep the outline over open ground.
              </p>
            </PanelSection>

            {result && (
              <>
                <PanelSection title="Drainage">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '10px' }}>
                    {Object.entries(DRAINAGE_MODES).map(([mode, label]) => (
                      <Button key={mode} variant={grading.mode === mode ? 'primary' : 'default'}
                        style={{ fontSize: '11px', padding: '6px' }} onClick={() => set({ mode })}>
                        {label}
                      </Button>
                    ))}
                  </div>

                  {grading.mode === 'oneway' && (
                    <>
                      <Slider label="Slope" suffix="%" min={0} max={3} step={0.1}
                        value={grading.slopePercent} onChange={(v) => set({ slopePercent: v })} />
                      <Slider label={`Drains toward ${bearingLabel(grading.bearing)}`} suffix="°" min={0} max={355} step={5}
                        value={grading.bearing} onChange={(v) => set({ bearing: v })} />
                    </>
                  )}
                  {grading.mode === 'crown' && (
                    <Slider label="Fall from center to each long side" suffix="%" min={0.25} max={3} step={0.25}
                      value={grading.crownPercent} onChange={(v) => set({ crownPercent: v })} />
                  )}
                  {grading.mode === 'best' && (
                    <>
                      <Slider label="Minimum slope" suffix="%" min={0} max={3} step={0.25}
                        value={grading.minSlopePercent}
                        onChange={(v) => set({ minSlopePercent: v, maxSlopePercent: Math.max(v, grading.maxSlopePercent) })} />
                      <Slider label="Maximum slope" suffix="%" min={0} max={3} step={0.25}
                        value={grading.maxSlopePercent}
                        onChange={(v) => set({ maxSlopePercent: v, minSlopePercent: Math.min(v, grading.minSlopePercent) })} />
                      <p style={{ ...muted, color: '#00ffaa' }}>
                        Best: drains toward {params.bearing}° ({bearingLabel(params.bearing)}) at {params.slopePercent}%
                      </p>
                      <Button variant="default" style={{ width: '100%', fontSize: '11px', marginBottom: '8px' }}
                        onClick={() => set({ mode: 'oneway', bearing: params.bearing, slopePercent: params.slopePercent })}>
                        Fine-tune this as a one-way slope
                      </Button>
                    </>
                  )}

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', margin: '6px 0' }}>
                    <span style={muted}>Pad height:</span>
                    {['balance', 'manual'].map(m => (
                      <label key={m} style={{ ...muted, color: '#a0b0c0', display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input type="radio" checked={grading.elevationMode === m}
                          onChange={() => set({
                            elevationMode: m,
                            manualElevation: grading.manualElevation ?? Math.round(active.padElevation * 10) / 10,
                          })} />
                        {m === 'balance' ? 'Balance cut & fill' : 'Set'}
                      </label>
                    ))}
                  </div>
                  {grading.elevationMode === 'manual' && (
                    <NumberInput label="Pad elevation at center" suffix="ft" step={0.1}
                      value={grading.manualElevation ?? Math.round(active.padElevation * 10) / 10}
                      onChange={(v) => set({ manualElevation: v })} />
                  )}
                </PanelSection>

                <PanelSection title="Compare (cubic yards)">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ color: '#6080a0', textAlign: 'right' }}>
                        <th style={{ textAlign: 'left', fontWeight: 400 }}>Option</th>
                        <th style={{ fontWeight: 400 }}>Cut</th>
                        <th style={{ fontWeight: 400 }}>Fill</th>
                        <th style={{ fontWeight: 400 }}>Deepest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(DRAINAGE_MODES).map(([mode, label]) => {
                        const s = result.scenarios[mode];
                        const isActive = grading.mode === mode;
                        return (
                          <tr key={mode} onClick={() => set({ mode })} style={{
                            cursor: 'pointer', textAlign: 'right',
                            color: isActive ? '#00c8ff' : '#c0d0e0',
                            background: isActive ? 'rgba(0,200,255,0.08)' : 'transparent',
                          }}>
                            <td style={{ textAlign: 'left', padding: '4px 2px' }}>{label}</td>
                            <td style={{ color: isActive ? undefined : '#ff9a70' }}>{yards(s.cutYards)}</td>
                            <td style={{ color: isActive ? undefined : '#70a8ff' }}>{yards(s.fillYards)}</td>
                            <td>{feet(Math.max(s.maxCut, s.maxFill))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p style={{ ...muted, marginTop: '6px' }}>Click a row to show it on the map.</p>
                </PanelSection>

                <PanelSection title="Selected option">
                  <div style={{ ...muted, color: '#a0b0c0' }}>
                    <div>Arena area: {Math.round(result.area).toLocaleString()} sq ft</div>
                    <div>Existing ground: {feet(result.existingMin)} to {feet(result.existingMax)}</div>
                    <div>Pad elevation at center: {active.padElevation.toFixed(2)}'</div>
                    <div>Cut {yards(active.cutYards)} yd³ (deepest {feet(active.maxCut)}) · Fill {yards(active.fillYards)} yd³ (deepest {feet(active.maxFill)})</div>
                    {result.missingArea > 0 && (
                      <div style={{ color: '#ffaa00' }}>
                        {Math.round(result.missingArea).toLocaleString()} sq ft of the outline has no survey data and is not counted.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Checkbox label="Show cut/fill map (red = cut, blue = fill)" checked={grading.showOverlay}
                      onChange={(v) => set({ showOverlay: v })} />
                  </div>
                  <Button variant="primary" onClick={onShow3D} style={{ width: '100%', marginTop: '6px' }}>
                    View in 3D
                  </Button>
                  <p style={{ ...muted, marginTop: '6px' }}>
                    Volumes are inside the outline only. They don't include side slopes beyond the edge, topsoil stripping,
                    footing/base material or compaction (fill usually needs 10–25% more dirt than it measures).
                  </p>
                </PanelSection>
              </>
            )}

            {!result && grading.roomId && (
              <p style={{ ...muted, color: '#ffaa00' }}>The outline doesn't overlap the elevation data.</p>
            )}
          </>
        )}
      </div>
    </FloatingPanel>
  );
};

export default GradingPanel;
